import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import GoogleReviews from "../components/GoogleReviews";
import ProjectModal from "../components/ProjectModal";

const STATS = [
  { value: "15+", label: "Years of Experience" },
  { value: "200+", label: "Projects Completed" },
  { value: "150+", label: "Happy Clients" },
];

const PLACEHOLDER_SERVICES = [
  { id: "1", icon: "🏛", name: "Architecture Design", description: "Comprehensive architectural solutions from concept to completion, blending aesthetics with functionality for residential and commercial projects." },
  { id: "2", icon: "🛋", name: "Interior Design", description: "Thoughtfully curated interiors that reflect your personality, balancing beauty, comfort, and purpose in every space." },
  { id: "3", icon: "🌿", name: "Landscape Design", description: "Creating outdoor environments that harmonise with nature and architecture, from private gardens to large-scale public spaces." },
  { id: "4", icon: "🏙", name: "Urban Planning", description: "Strategic planning for sustainable urban developments that foster community, connectivity, and long-term livability." },
  { id: "5", icon: "✏️", name: "Renovation & Remodelling", description: "Breathing new life into existing spaces with sensitive renovations that honour the original character while meeting modern needs." },
  { id: "6", icon: "📐", name: "Structural Consulting", description: "Expert structural analysis and consulting services ensuring safety, compliance, and integrity across all project types." },
];

const PLACEHOLDER_PROJECTS = [
  { id: "1", title: "Kia India — Pan India Showrooms", category: "commercial", year: 2023, coverImage: "/projects/kia_1.jpeg", description: "Designed and executed Kia showrooms across India with a consistent brand identity and premium spatial planning.", images: ["/projects/kia_1.jpeg", "/projects/kia_2.jpeg", "/projects/kia_3.jpeg"] },
  { id: "2", title: "Hero 2.0 — Showrooms & Workshops", category: "commercial", year: 2024, coverImage: "/projects/hero_2.jpeg", description: "Completed 40+ Hero and Premia showrooms and workshops across India in 6 months.", images: ["/projects/hero_2.jpeg", "/projects/hero_3.jpeg", "/projects/hero_4.jpeg"] },
  { id: "3", title: "Okinawa Galaxy — Flagship Store", category: "commercial", year: 2023, coverImage: "/projects/okinawa_galaxy_1.jpeg", description: "Premium experiential flagship showroom designed to showcase the full Okinawa electric vehicle lineup.", images: ["/projects/okinawa_galaxy_1.jpeg", "/projects/okinawa_galaxy_2.jpeg", "/projects/okinawa_galaxy_3.jpeg"] },
  { id: "4", title: "MAPL Group Office — Noida", category: "interior", year: 2023, coverImage: "/projects/mapl_office_1.jpeg", description: "Corporate office design for MAPL Group — collaborative zones, executive suites, and brand-forward interiors.", images: ["/projects/mapl_office_1.jpeg", "/projects/mapl_office_2.jpeg", "/projects/mapl_office_4.jpeg"] },
  { id: "5", title: "Sai Shreeja Villa — Vasant Kunj", category: "residential", year: 2024, coverImage: "/projects/sai_shreeja_2.jpeg", description: "Luxurious villa featuring a grand entrance, private temple, courtyard, and premium suite interiors.", images: ["/projects/sai_shreeja_2.jpeg", "/projects/sai_shreeja_3.jpeg", "/projects/sai_shreeja_4.jpeg"] },
  { id: "6", title: "Penthouse — Noida", category: "residential", year: 2024, coverImage: "/projects/penthouse_2.jpeg", description: "High-end penthouse with panoramic views, premium finishes, and seamless indoor-outdoor flow.", images: ["/projects/penthouse_2.jpeg", "/projects/penthouse_3.jpeg", "/projects/penthouse_4.jpeg"] },
];

const TESTIMONIALS = [
  { quote: "Long-term collaboration built on reliability and consistent quality. The team at Ark Design delivered our showroom network across India with precision and creativity.", author: "Kia Motors", role: "Pan India Partner" },
  { quote: "We have completed Hero and Premia showrooms across India in record time. Their dedication and attention to detail reflect true commitment to excellence.", author: "Hero MotoCorp", role: "Showroom Partner" },
  { quote: "Completed 200 showrooms in one year including flagship Galaxy stores. Along with architecture, they provided furniture and branding — a truly end-to-end partner.", author: "Okinawa Autotech", role: "200 Showrooms Pan India" },
  { quote: "Partnering with ADI has been a seamless experience. Their design-build approach ensured full accountability and flawless execution on our commercial project.", author: "Aadharshila Construction", role: "Construction Partner" },
];

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=1600&q=80",
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&q=80",
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1600&q=80",
];

export default function Home() {
  const [heroIdx, setHeroIdx] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);
  const [testIdx, setTestIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setHeroIdx(i => (i + 1) % HERO_IMAGES.length), 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTestIdx(i => (i + 1) % TESTIMONIALS.length), 4000);
    return () => clearInterval(t);
  }, []);

  const heroBg = HERO_IMAGES[heroIdx];

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section
        className="relative h-screen flex items-end"
        style={{
          backgroundImage: `url(${heroBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pb-20 w-full fade-in-up">
          <div className="w-12 h-0.5 bg-white mb-6" />
          <h1
            className="text-4xl md:text-6xl lg:text-7xl text-white font-normal leading-tight max-w-3xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Designing Spaces That Inspire
          </h1>
          <p className="text-gray-300 mt-4 text-sm tracking-widest uppercase">
            Architecture · Interior · Landscape · Noida
          </p>
          <div className="flex gap-4 mt-8">
            <Link
              to="/projects"
              className="text-xs tracking-widest uppercase font-medium px-6 py-3 bg-white text-gray-900 hover:bg-gray-100 transition-colors"
            >
              View Projects
            </Link>
            <Link
              to="/contact"
              className="text-xs tracking-widest uppercase font-medium px-6 py-3 border border-white text-white hover:bg-white hover:text-gray-900 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 grid grid-cols-3 gap-8">
          {STATS.map(({ value, label }) => (
            <div key={label} className="text-center">
              <div
                className="text-3xl md:text-4xl font-semibold text-gray-900"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {value}
              </div>
              <div className="text-xs tracking-widest uppercase text-gray-500 mt-1">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="py-20 md:py-28" style={{ background: "var(--color-bg-soft)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-xs tracking-widest uppercase text-gray-400 mb-3">What We Do</div>
          <h2
            className="text-3xl md:text-4xl font-normal text-gray-900 mb-12 max-w-xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Our Services
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PLACEHOLDER_SERVICES.slice(0, 6).map(svc => (
              <div
                key={svc.id}
                className="bg-white p-8 border border-gray-100 hover:border-gray-300 hover:shadow-sm transition-all"
              >
                <div className="text-3xl mb-4">{svc.icon}</div>
                <h3
                  className="text-lg font-medium text-gray-900 mb-2"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  {svc.name}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">{svc.description}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/services"
              className="text-xs tracking-widest uppercase font-medium border border-gray-900 text-gray-900 px-6 py-3 hover:bg-gray-900 hover:text-white transition-colors"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-xs tracking-widest uppercase text-gray-400 mb-3">Our Work</div>
          <h2
            className="text-3xl md:text-4xl font-normal text-gray-900 mb-12 max-w-xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Selected Projects
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {PLACEHOLDER_PROJECTS.map(proj => (
                <div
                  key={proj.id}
                  className="group cursor-pointer"
                  onClick={() => setSelectedProject(proj)}
                >
                  <div className="relative overflow-hidden aspect-[4/3] bg-gray-100">
                    {proj.coverImage ? (
                      <img
                        src={proj.coverImage}
                        alt={proj.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300 text-4xl">🏛</div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-xs tracking-widest uppercase">View Project</span>
                    </div>
                  </div>
                  <div className="mt-3">
                    <h3 className="text-sm font-medium text-gray-900" style={{ fontFamily: "var(--font-heading)" }}>
                      {proj.title}
                    </h3>
                    <p className="text-xs text-gray-400 tracking-wide mt-0.5 capitalize">
                      {proj.category} · {proj.year}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          <div className="mt-10 text-center">
            <Link
              to="/projects"
              className="text-xs tracking-widest uppercase font-medium border border-gray-900 text-gray-900 px-6 py-3 hover:bg-gray-900 hover:text-white transition-colors"
            >
              View All Projects
            </Link>
          </div>
        </div>
      </section>

      {/* Google Reviews */}
      <GoogleReviews />

      {/* Trusted Clients — infinite marquee slider */}
      <section className="py-20 md:py-28 overflow-hidden" style={{ background: "var(--color-bg-soft)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-10">
          <div className="text-xs tracking-widest uppercase text-gray-400 mb-3">Trusted By</div>
          <h2
            className="text-3xl md:text-4xl font-normal text-gray-900 mb-2 max-w-xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Industry Leaders Who Trust Us
          </h2>
          <p className="text-sm text-gray-500">
            Over 200+ projects completed Pan-India across automotive, retail, institutional and residential sectors.
          </p>
        </div>
        {/* Marquee track — duplicated for seamless loop */}
        <div className="relative">
          <div className="flex gap-8 reviews-track" style={{ width: "max-content" }}>
            {[
              { src: "/clients/client_1_269x284.jpeg", alt: "Kia Motors" },
              { src: "/clients/client_2_365x109.png",  alt: "Hero MotoCorp" },
              { src: "/clients/client_3_281x128.jpeg", alt: "MG Motors" },
              { src: "/clients/client_4_248x80.png",   alt: "Okinawa Autotech" },
              { src: "/clients/client_5_197x168.jpeg", alt: "Volvo Eicher" },
              { src: "/clients/client_7_331x58.png",   alt: "Altigreen" },
              { src: "/clients/client_8_300x169.png",  alt: "Ampere EV" },
              { src: "/clients/client_9_251x90.png",   alt: "MAPL Group" },
              { src: "/clients/client_10_323x323.png", alt: "Fitblis" },
              { src: "/clients/client_11_200x112.jpeg",alt: "Matter Motors" },
              // duplicate for seamless loop
              { src: "/clients/client_1_269x284.jpeg", alt: "Kia Motors 2" },
              { src: "/clients/client_2_365x109.png",  alt: "Hero MotoCorp 2" },
              { src: "/clients/client_3_281x128.jpeg", alt: "MG Motors 2" },
              { src: "/clients/client_4_248x80.png",   alt: "Okinawa Autotech 2" },
              { src: "/clients/client_5_197x168.jpeg", alt: "Volvo Eicher 2" },
              { src: "/clients/client_7_331x58.png",   alt: "Altigreen 2" },
              { src: "/clients/client_8_300x169.png",  alt: "Ampere EV 2" },
              { src: "/clients/client_9_251x90.png",   alt: "MAPL Group 2" },
              { src: "/clients/client_10_323x323.png", alt: "Fitblis 2" },
              { src: "/clients/client_11_200x112.jpeg",alt: "Matter Motors 2" },
            ].map(c => (
              <div key={c.alt} className="flex-shrink-0 flex items-center justify-center p-4 bg-white border border-gray-100 hover:border-gray-300 transition-all" style={{ width: "160px", height: "90px" }}>
                <img
                  src={c.src}
                  alt={c.alt}
                  className="max-h-14 max-w-full object-contain grayscale hover:grayscale-0 transition-all duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials — auto-advancing slider */}
      <section className="py-20 md:py-28" style={{ background: "var(--color-bg-soft)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-xs tracking-widest uppercase text-gray-400 mb-3">Testimonials</div>
          <h2
            className="text-3xl md:text-4xl font-normal text-gray-900 mb-10 max-w-xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Built on Trust and Collaboration
          </h2>

          {/* Active card — no absolute positioning, just show/hide */}
          <div className="max-w-3xl">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="transition-all duration-500"
                style={{ display: i === testIdx ? "block" : "none" }}
              >
                <div className="bg-white border border-gray-200 p-10 shadow-sm">
                  {/* Big decorative quote */}
                  <div
                    className="text-8xl leading-none text-gray-200 mb-2 select-none"
                    style={{ fontFamily: "Georgia, serif", lineHeight: 1 }}
                  >
                    &ldquo;
                  </div>
                  <p className="text-lg text-gray-700 leading-relaxed mb-8 italic">
                    {t.quote}
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-0.5 bg-gray-900" />
                    <div>
                      <div className="text-sm font-semibold text-gray-900" style={{ fontFamily: "var(--font-heading)" }}>
                        {t.author}
                      </div>
                      <div className="text-xs tracking-widest uppercase text-gray-400 mt-0.5">
                        {t.role}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Dot indicators */}
            <div className="flex gap-3 mt-6">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setTestIdx(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === testIdx ? "w-10 bg-gray-900" : "w-4 bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28" style={{ backgroundColor: "var(--color-footer-bg)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2
            className="text-3xl md:text-4xl font-normal text-white mb-4"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Ready to Build Your Dream Space?
          </h2>
          <p className="text-gray-400 text-sm mb-8 tracking-wide">
            Let's talk about your project. We'd love to hear from you.
          </p>
          <Link
            to="/contact"
            className="text-xs tracking-widest uppercase font-medium px-8 py-4 bg-white text-gray-900 hover:bg-gray-100 transition-colors"
          >
            Get in Touch
          </Link>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />

      {selectedProject && (
        <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
      )}
    </div>
  );
}
